import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/dialog';
import { Redo2, Undo2 } from 'lucide-react';
import { useState } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@Components/AlertDialog';
import { cn } from '@Utils/cn';

interface HeaderProps {
  currentPageName: string;
  pages: { name: string; id: string }[];
  handlePageChange: (pageId: string) => void;
  /** Page has ever been published. */
  isPublished?: boolean;
  /** Local draft differs from the published baseline. */
  isDirty?: boolean;
  /** Local draft differs from the db-draft baseline ("Save draft" enabled). */
  hasUnsavedChanges?: boolean;
  canUndo?: boolean;
  canRedo?: boolean;
  /** A save/publish/discard request is in flight. */
  isBusy?: boolean;
  /**
   * User holds CONTENT_WRITE. When false the workflow actions (Save draft /
   * Publish / Discard) are hidden — the page is view-only. Default true.
   */
  canWrite?: boolean;
  onUndo?: () => void;
  onRedo?: () => void;
  onSaveDraft?: () => void;
  onPublish?: () => void;
  onDiscard?: () => void;
}

function StatusBadge({
  isPublished,
  isDirty,
}: {
  isPublished?: boolean;
  isDirty?: boolean;
}) {
  const label = !isPublished
    ? 'DRAFT'
    : isDirty
      ? 'UNPUBLISHED CHANGES'
      : 'LIVE';
  return (
    <span
      className={cn(
        'rounded-full px-1.5 text-xs whitespace-nowrap',
        label === 'LIVE' && 'bg-green-100 text-green-700',
        label === 'UNPUBLISHED CHANGES' && 'bg-amber-100 text-amber-700',
        label === 'DRAFT' && 'bg-zinc-100 text-zinc-600',
      )}
    >
      {label}
    </span>
  );
}

export default function Header({
  currentPageName,
  pages,
  handlePageChange,
  isPublished,
  isDirty,
  hasUnsavedChanges,
  canUndo,
  canRedo,
  isBusy,
  canWrite = true,
  onUndo,
  onRedo,
  onSaveDraft,
  onPublish,
  onDiscard,
}: HeaderProps) {
  const [pagesDialogOpen, setPagesDialogOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  return (
    <>
      <div className="flex justify-between border-b border-gray-200 p-2">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <span className="px-2">Pages</span>
            <span>/</span>

            <button
              onClick={() => setPagesDialogOpen(true)}
              className="rounded-lg px-2 hover:bg-zinc-100"
            >
              {currentPageName}
            </button>
          </div>
          <div className="flex items-center">
            <StatusBadge isPublished={isPublished} isDirty={isDirty} />
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Undo"
            title="Undo (Ctrl+Z)"
            disabled={!canUndo}
            onClick={onUndo}
            className="rounded-lg p-1.5 text-zinc-600 hover:bg-zinc-100 disabled:opacity-40 disabled:hover:bg-transparent"
          >
            <Undo2 size={16} />
          </button>
          <button
            type="button"
            aria-label="Redo"
            title="Redo (Ctrl+Shift+Z)"
            disabled={!canRedo}
            onClick={onRedo}
            className="mr-1 rounded-lg p-1.5 text-zinc-600 hover:bg-zinc-100 disabled:opacity-40 disabled:hover:bg-transparent"
          >
            <Redo2 size={16} />
          </button>
          {canWrite && (
            <>
              {isDirty && isPublished && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={isBusy}
                  onClick={() => setDiscardConfirmOpen(true)}
                >
                  Discard changes
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                disabled={!hasUnsavedChanges || isBusy}
                onClick={onSaveDraft}
              >
                Save draft
              </Button>
              <Button
                size="sm"
                disabled={!isDirty || isBusy}
                onClick={onPublish}
              >
                Publish
              </Button>
            </>
          )}
        </div>
      </div>

      <Dialog open={pagesDialogOpen} onOpenChange={setPagesDialogOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Select Page</DialogTitle>
          </DialogHeader>

          <div>
            {pages.map((page) => (
              <div key={page.id} className="flex items-center justify-between">
                <span>{page.name}</span>
                <Button
                  size="sm"
                  onClick={() => {
                    handlePageChange(page.id);
                    setPagesDialogOpen(false);
                  }}
                >
                  Select
                </Button>
              </div>
            ))}
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="link">
                Cancel
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={discardConfirmOpen}
        onOpenChange={setDiscardConfirmOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard draft changes?</AlertDialogTitle>
            <AlertDialogDescription>
              This resets the page to its last published version. All
              unpublished edits are lost and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                onDiscard?.();
                setDiscardConfirmOpen(false);
              }}
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
